import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent,
  Alert,
  Autocomplete,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { locationsApi, productsApi, organizationsApi, beaconAssignmentsApi, locationAssignmentsApi, locationEquivalenciesApi } from '../../api/services';
import type { LocationAssignment, CreateAssignmentData } from '../../api/config';
import { TagIdManager, TagEdits } from './TagIdManager';
import { FormDialog } from '../FormDialog';
import { useAuth } from '../../contexts/AuthContext';

interface LocationAssignmentFormProps {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  onSubmit: (data: CreateAssignmentData & {
    tagIds?: string[];
    removedTagIds?: string[];
    newTagsToCreate?: string[];
    product_equivalency_id?: number;
    group_max_count?: number;
  }) => void;
  assignment?: LocationAssignment;
  locationId?: string;
  sku?: string;
  initialOrganizationId?: string;
  initialLocationId?: string;
  isLoading?: boolean;
  error?: string | null;
}

export const LocationAssignmentForm: React.FC<LocationAssignmentFormProps> = ({
  open,
  onClose,
  onExited,
  onSubmit,
  assignment,
  locationId,
  sku,
  initialOrganizationId,
  initialLocationId,
  isLoading = false,
  error: apiError,
}) => {
  const { user } = useAuth();
  const isEdit = !!assignment;

  const [formData, setFormData] = React.useState<CreateAssignmentData>({
    location_id: locationId ? Number(locationId) : assignment?.location_id || 0,
    sku: sku || assignment?.sku || '',
    expected_count: assignment?.expected_count || undefined,
    reorder_threshold: assignment?.reorder_threshold || undefined,
    assigned_by: assignment?.assigned_by || user?.name || '',
    notes: assignment?.notes || '',
  });

  const [groupMaxCount, setGroupMaxCount] = React.useState<number | undefined>(undefined);

  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<string>('');
  const [tagEdits, setTagEdits] = React.useState<TagEdits>({
    currentTags: [],
    addedTags: [],
    removedTags: [],
    newTagsToCreate: [],
    hasErrors: false,
  });

  // Fetch organizations, locations and products for dropdowns
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations'],
    queryFn: organizationsApi.getAll,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations', selectedOrganizationId],
    queryFn: () => selectedOrganizationId
      ? locationsApi.getAll({ organization_id: selectedOrganizationId })
      : locationsApi.getAllWithoutFilters(),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.getAll,
  });

  // Look up the selected product's product_equivalency_id
  const selectedProduct = React.useMemo(
    () => products.find(p => p.sku === formData.sku),
    [products, formData.sku]
  );
  const productEquivalencyId = selectedProduct?.product_equivalency?.id;

  // Fetch existing equivalency setting for this location + product equivalency to pre-populate max_count
  const { data: groupSettings = [] } = useQuery({
    queryKey: ['location_equivalencies_for_form', formData.location_id, productEquivalencyId],
    queryFn: () => locationEquivalenciesApi.getAll({
      location_id: formData.location_id,
      product_equivalency_id: productEquivalencyId!,
    }),
    enabled: open && !!formData.location_id && !!productEquivalencyId,
  });

  // Pre-populate groupMaxCount from the existing equivalency setting when data
  // loads. The else-branch clears stale state when the operator switches to a
  // grouped product that has no LPE setting yet.
  React.useEffect(() => {
    if (groupSettings.length > 0) {
      setGroupMaxCount(groupSettings[0].max_count ?? undefined);
    } else if (formData.location_id && productEquivalencyId) {
      setGroupMaxCount(undefined);
    }
  }, [groupSettings, formData.location_id, productEquivalencyId]);

  // Fetch beacon assignments at this location for this SKU (when editing)
  const { data: assignedToLocationBeacons = [] } = useQuery({
    queryKey: ['beacon_assignments_at_location', formData.sku, formData.location_id],
    queryFn: () => beaconAssignmentsApi.atLocation(formData.location_id, { sku: formData.sku }),
    enabled: open && !!formData.sku && !!formData.location_id,
  });

  // Fetch available beacon assignments for the SKU (not assigned to any location yet)
  const { data: availableBeacons = [] } = useQuery({
    queryKey: ['beacon_assignments_available', formData.sku],
    queryFn: () => beaconAssignmentsApi.availableForLocation(formData.sku),
    enabled: open && !!formData.sku,
  });

  // Check for duplicate assignment (only for new assignments)
  const { data: isDuplicate = false } = useQuery({
    queryKey: ['location_assignment_exists', formData.location_id, formData.sku],
    queryFn: () => locationAssignmentsApi.checkExists(formData.location_id, formData.sku),
    enabled: open && !isEdit && !!formData.location_id && !!formData.sku,
  });

  // Memoize tag IDs to prevent unnecessary re-renders in TagIdManager
  // Assigned tags: tags currently at this location for this product
  const assignedTagIds = React.useMemo(
    () => assignedToLocationBeacons.map(ba => ba.beacon_id),
    [assignedToLocationBeacons]
  );

  // Available tags: tags assigned to product but not to any location (can be "pulled" in)
  const availableTagIds = React.useMemo(
    () => availableBeacons.map(ba => ba.beacon_id),
    [availableBeacons]
  );

  // Initialize form state when the dialog opens.
  // Props (assignment, locationId, sku) are already set before `open` flips
  // to true, so keying on [open] alone is correct and avoids re-firing
  // during the close animation or on unrelated prop changes.
  React.useEffect(() => {
    if (!open) return;

    if (assignment) {
      setFormData({
        location_id: assignment.location_id,
        sku: assignment.sku,
        expected_count: assignment.expected_count,
        reorder_threshold: assignment.reorder_threshold,
        assigned_by: assignment.assigned_by || '',
        notes: assignment.notes || '',
      });
      // Set organization based on assignment's location
      if (assignment.location?.organization_id) {
        setSelectedOrganizationId(assignment.location.organization_id.toString());
      }
    } else {
      setSelectedOrganizationId(initialOrganizationId || '');
      setGroupMaxCount(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset form when form opens for new assignment
  React.useEffect(() => {
    if (open && !assignment && !locationId) {
      setSelectedOrganizationId(initialOrganizationId || '');
      setGroupMaxCount(undefined);
      // Reset form data with current user's name for new assignments
      setFormData({
        location_id: initialLocationId ? Number(initialLocationId) : 0,
        sku: sku || '',
        expected_count: undefined,
        reorder_threshold: undefined,
        assigned_by: user?.name || '',
        notes: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (tagEdits.hasErrors) {
      return;
    }

    onSubmit({
      ...formData,
      tagIds: tagEdits.addedTags,
      removedTagIds: tagEdits.removedTags,
      newTagsToCreate: tagEdits.newTagsToCreate,
      product_equivalency_id: productEquivalencyId,
      group_max_count: groupMaxCount,
    });
  };

  const handleTextFieldChange = (field: keyof CreateAssignmentData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'expected_count' || field === 'reorder_threshold'
        ? (value === '' ? undefined : Number(value))
        : value,
    }));
  };

  const handleSelectChange = (field: keyof CreateAssignmentData) => (
    e: SelectChangeEvent<string>
  ) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'location_id' ? Number(value) : value,
    }));
  };

  const handleOrganizationChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setSelectedOrganizationId(value);
    setFormData(prev => ({
      ...prev,
      location_id: 0,
    }));
  };

  const isLocationSpecific = !!locationId;
  const isProductSpecific = !!sku;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={isEdit ? 'Edit Assignment' : 'Create Assignment'}
      submitLabel={isEdit ? 'Update' : 'Create'}
      error={apiError}
      isPending={isLoading}
      submitDisabled={
        tagEdits.hasErrors ||
        !formData.location_id ||
        !formData.sku ||
        (!isEdit && isDuplicate)
      }
      TransitionProps={{ onExited }}
    >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Organization Selection */}
            {!isLocationSpecific && (
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={selectedOrganizationId}
                  onChange={handleOrganizationChange}
                  label="Organization"
                >
                  <MenuItem value="">All Organizations</MenuItem>
                  {organizations.map((organization) => (
                    <MenuItem key={organization.id} value={organization.id.toString()}>
                      {organization.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Location Selection */}
            {!isLocationSpecific && (
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={formData.location_id.toString()}
                  onChange={handleSelectChange('location_id')}
                  label="Location"
                  required
                >
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={location.id.toString()}>
                      {location.name} ({location.slug})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Product Selection */}
            {!isProductSpecific && (
              <Autocomplete
                options={products}
                value={products.find(p => p.sku === formData.sku) ?? null}
                onChange={(_e, product) => {
                  setFormData(prev => ({
                    ...prev,
                    sku: product?.sku ?? '',
                  }));
                }}
                getOptionLabel={(option) => `${option.sku} (${option.name})`}
                isOptionEqualToValue={(option, value) => option.sku === value.sku}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    required
                  />
                )}
                fullWidth
              />
            )}

            {/* Group max (total units) — saved to product equivalency setting, not location assignment */}
            <TextField
              label="Max (total units)"
              type="number"
              value={groupMaxCount ?? ''}
              onChange={(e) => setGroupMaxCount(e.target.value === '' ? undefined : Number(e.target.value))}
              fullWidth
              inputProps={{ min: 0 }}
              helperText={selectedProduct?.product_equivalency
                ? `Applies to product equivalency: ${selectedProduct.product_equivalency.name}`
                : undefined
              }
            />

            {/* Assigned By */}
            <TextField
              label="Assigned By"
              value={formData.assigned_by}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              helperText="Automatically set to current user"
            />

            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={handleTextFieldChange('notes')}
              fullWidth
              multiline
              rows={3}
            />
            {/* Duplicate assignment warning - only show for new assignments */}
            {!isEdit && isDuplicate && (
              <Alert severity="error">
                This product is already assigned to this location. Please edit the existing assignment instead.
              </Alert>
            )}

            {/* Tag ID Management */}
            <TagIdManager
              key={`${assignment?.id ?? 'new'}-${formData.sku}-${formData.location_id}`}
              initialAssignedTagIds={assignedTagIds}
              availableTagIds={availableTagIds}
              onTagEditsChange={setTagEdits}
              disabled={isLoading}
              isEdit={isEdit}
            />

            {/* Display current assignment info if editing */}
            {isEdit && assignment && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Current Assignment:</strong><br />
                  Location: {assignment.location?.name || assignment.location_id} ({assignment.location?.slug})<br />
                  Product: {assignment.product?.name || assignment.sku}<br />
                  Status: {assignment.status}<br />
                  Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            {/* No tags warning */}
            {!isEdit && tagEdits.currentTags.length === 0 && (
              <Alert severity="warning">
                No tags are assigned. You can still create this assignment and add tags later.
              </Alert>
            )}
          </Box>
    </FormDialog>
  );
};
